import type { CSSProperties } from 'react';
import { stripLeadingInstructionDayLabel } from '@utils/ganttDataGenerator';

function formatText(text: string): JSX.Element[] {
	const parts: JSX.Element[] = [];
	let lastIndex = 0;

	const patterns = [
		/(Days? \d+-\d+|Day \d+|\d+-\d+ hours?)/gi,
		/(\d+(?:\.\d+)?(?:-\d+(?:\.\d+)?)?\s*(?:mm|cm|m|kg|bags?|pcs?|m²|m³|lm|times?|weeks?|months?))/gi,
		/(\d+(?:\.\d+)?%)/gi,
		/(\d+(?:\.\d+)?\s*x\s*\d+(?:\.\d+)?)/gi,
		/(\d+-\d+-\d+)/g,
		/(\([^)]+\))/g
	];

	const matches: Array<{ start: number; end: number; text: string }> = [];
	patterns.forEach((pattern) => {
		let match;
		while ((match = pattern.exec(text)) !== null) {
			matches.push({ start: match.index, end: match.index + match[0].length, text: match[0] });
		}
	});
	matches.sort((a, b) => a.start - b.start);

	const nonOverlapping: Array<{ start: number; end: number; text: string }> = [];
	for (const match of matches) {
		if (nonOverlapping.length === 0 || match.start >= nonOverlapping[nonOverlapping.length - 1].end) {
			nonOverlapping.push(match);
		}
	}

	nonOverlapping.forEach((match, idx) => {
		if (match.start > lastIndex) {
			parts.push(<span key={`text-${idx}`}>{text.substring(lastIndex, match.start)}</span>);
		}
		const isParentheses = match.text.startsWith('(');
		const isTimeframe = /Days?|hours?/i.test(match.text);
		const highlightStyle: CSSProperties = isParentheses
			? { color: '#198754', opacity: 0.8 }
			: isTimeframe
				? { color: '#157347' }
				: { color: '#198754' };
		parts.push(
			<span key={`highlight-${idx}`} className={isParentheses ? 'fw-semibold' : 'fw-bold'} style={highlightStyle}>
				{match.text}
			</span>
		);
		lastIndex = match.end;
	});

	if (lastIndex < text.length) {
		parts.push(<span key="text-end">{text.substring(lastIndex)}</span>);
	}
	if (parts.length === 0) {
		return [<span key="full">{text}</span>];
	}
	return parts;
}

/** Format a single implementation instruction line for read-only display. */
export function formatPlanInstruction(instruction: string): JSX.Element {
	const colonIndex = instruction.indexOf(':');
	if (colonIndex === -1) {
		return <span>{formatText(instruction)}</span>;
	}
	const title = instruction.substring(0, colonIndex);
	const description = stripLeadingInstructionDayLabel(instruction.substring(colonIndex + 1));
	return (
		<>
			<span className="fw-bold" style={{ color: '#198754' }}>
				{formatText(title)}:
			</span>
			<span> {formatText(description)}</span>
		</>
	);
}
